import { useState } from "react";
import { Share2, X, Twitter, Facebook, MessageCircle, Copy, Check, Send } from "lucide-react";

/* Portable share-sheet — native navigator.share when available, otherwise
   opens Twitter / Facebook / WhatsApp / Telegram share intents + copy link. */

export default function ShareBoard({ url, title, text, testid = "share-board" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const abs = typeof window !== "undefined" && !url?.startsWith("http")
    ? window.location.origin + (url || "/")
    : (url || "/");
  const message = text || title || "Check this out on My Social Pulse";

  const trigger = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title, text: message, url: abs });
        return;
      } catch { /* fall through to modal */ }
    }
    setOpen(true);
  };

  const links = [
    { key: "twitter",  label: "X / Twitter", Icon: Twitter,       color: "bg-slate-900 text-white",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(abs)}` },
    { key: "facebook", label: "Facebook",    Icon: Facebook,      color: "bg-blue-600 text-white",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(abs)}` },
    { key: "whatsapp", label: "WhatsApp",    Icon: MessageCircle, color: "bg-emerald-500 text-white",
      href: `https://wa.me/?text=${encodeURIComponent(message + " " + abs)}` },
    { key: "telegram", label: "Telegram",    Icon: Send,          color: "bg-sky-500 text-white",
      href: `https://t.me/share/url?url=${encodeURIComponent(abs)}&text=${encodeURIComponent(message)}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(abs);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  return (
    <>
      <button onClick={trigger} data-testid={`${testid}-trigger`} aria-label="Share"
        className="inline-grid place-items-center w-8 h-8 rounded-full bg-white/95 text-slate-600 hover:text-blue-600 hover:bg-white shadow-sm">
        <Share2 className="w-4 h-4" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] grid place-items-end sm:place-items-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)} data-testid={`${testid}-modal`}>
          <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <div className="font-serif font-800 text-lg text-blue-900">Share</div>
              <button onClick={() => setOpen(false)} data-testid={`${testid}-close`}
                className="w-8 h-8 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 pt-4 pb-2 text-sm text-slate-600 truncate">{title}</div>
            <div className="grid grid-cols-4 gap-3 px-5 pb-4">
              {links.map(({ key, label, Icon, color, href }) => (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-${key}`}
                  className="flex flex-col items-center gap-2 text-xs font-700 text-slate-700 hover:text-blue-600">
                  <span className={`w-12 h-12 rounded-2xl grid place-items-center ${color}`}><Icon className="w-5 h-5" /></span>
                  {label}
                </a>
              ))}
            </div>
            <button onClick={copy} data-testid={`${testid}-copy`}
              className="w-full flex items-center gap-3 px-5 py-4 border-t border-slate-100 hover:bg-slate-50">
              <span className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 grid place-items-center">
                {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
              </span>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-700 text-slate-900">{copied ? "Link copied!" : "Copy link"}</div>
                <div className="text-xs text-slate-500 truncate">{abs}</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
